import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/kit";

const hash = (s) => { s = String(s); let h = 0; for (let i = 0; i < s.length; i++) { h = (h << 5) - h + s.charCodeAt(i); h |= 0; } return Math.abs(h); };

const BADGES = {
  Popular: { icon: "Flame", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  Trending: { icon: "TrendingUp", cls: "bg-rose-500/15 text-rose-400 border-rose-500/30" },
  "In Demand": { icon: "Zap", cls: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
};

// Deterministic, clearly-illustrative demo engagement signals derived from a stable seed.
export function hotFor(seed) {
  const h = hash(seed);
  return {
    badge: ["Popular", "Trending", "In Demand", "Popular", null][h % 5],
    weekViews: 60 + (h % 340),
    online: 3 + (h % 22),
    booked: 4 + (h % 28),
    spots: 1 + (h % 4),
  };
}

export function HotBadge({ badge, className = "" }) {
  if (!badge || !BADGES[badge]) return null;
  const b = BADGES[badge];
  return (
    <span data-testid={`hot-badge-${badge.replace(/\s/g, "-").toLowerCase()}`}
      className={`inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 border ${b.cls} ${className}`}>
      <Icon name={b.icon} size={11} /> {badge}
    </span>
  );
}

export function LivePulse({ label, className = "" }) {
  return (
    <span data-testid="live-pulse" className={`inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 ${className}`}>
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
      </span>
      {label}
    </span>
  );
}

export function SocialProof({ icon = "Users", children, className = "" }) {
  return (
    <span data-testid="social-proof" className={`inline-flex items-center gap-1 text-[11px] text-slate-400 ${className}`}>
      <Icon name={icon} size={12} className="text-amber-400/80" /> {children}
    </span>
  );
}

const pad = (n) => String(n).padStart(2, "0");
function fmt(ms) {
  if (ms < 0) ms = 0;
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return (d > 0 ? d + "d " : "") + `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export function Countdown({ hours = 24, seed = "x", to = null, label = "Ends in", icon = "Timer", className = "" }) {
  const span = hours * 3600 * 1000;
  const [target] = useState(() => (to ? new Date(to).getTime() : Date.now() + span - (hash(seed) % Math.floor(span / 2 + 1))));
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const left = target - now;
  return (
    <span data-testid="countdown" className={`inline-flex items-center gap-1.5 text-xs font-semibold ${className}`}>
      <Icon name={icon} size={13} className="text-amber-400" />
      {left <= 0 ? <span className="font-bold text-emerald-400">Live now</span> : (<><span className="text-slate-400">{label}</span><span className="tabular-nums font-bold text-amber-400">{fmt(left)}</span></>)}
    </span>
  );
}

// One-time, dismissible nudge that appears after `delay` ms of inactivity on a page.
export function LiveNudge({ id, title, message, ctaLabel, to, onCta, delay = 30000 }) {
  const nav = useNavigate();
  const key = `mfx_nudge_${id}`;
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (sessionStorage.getItem(key)) return;
    const t = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(t);
  }, [key, delay]);
  const close = () => { sessionStorage.setItem(key, "1"); setShow(false); };
  if (!show) return null;
  return (
    <div data-testid="live-nudge" className="fixed bottom-4 left-4 z-[80] w-[320px] max-w-[calc(100vw-2rem)] fade-up">
      <div className="rounded-2xl bg-[#12151F] border border-amber-500/30 shadow-[0_16px_40px_rgba(0,0,0,.5)] p-4">
        <div className="flex items-start gap-2">
          <span className="w-8 h-8 rounded-xl bg-amber-500/15 grid place-items-center shrink-0"><Icon name="Flame" size={16} className="text-amber-400" /></span>
          <div className="min-w-0">
            <div className="font-head font-semibold text-sm text-slate-100">{title}</div>
            <p className="text-xs text-slate-400 mt-0.5">{message}</p>
          </div>
          <button onClick={close} data-testid="live-nudge-close" className="ml-auto text-slate-500 hover:text-slate-200 shrink-0"><Icon name="X" size={16} /></button>
        </div>
        {ctaLabel && <button onClick={() => { close(); if (onCta) onCta(); else if (to) nav(to); }} data-testid="live-nudge-cta"
          className="mt-3 w-full grad-amber text-black rounded-xl py-2 text-sm font-semibold amber-glow">{ctaLabel}</button>}
      </div>
    </div>
  );
}
